import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaSearch, FaRegEye, FaTrash, FaImage, FaUpload, FaEdit, FaTimes, FaPlus, FaMinus } from 'react-icons/fa';
import { HiOutlineRefresh } from 'react-icons/hi';

import Loader from '../../components/Loader';
import NotFound from '../../components/UI/NotFound';
import FileUpload from '../../components/UI/FileUpload';
import { formatView } from '../../components/formatView';
import Calling from '../../api/Calling';
import generateCaptcha from '../../components/captcha';
import { imageType, MAX_FILE_SIZE_IMAGE, invalidImageMessage, imageSizeExceededMessage } from '../../components/imageType';
import { TbNetwork } from 'react-icons/tb';

const Blog = () => {
    const navigate = useNavigate();

    const [loader, setLoader] = useState(false);
    const [blogs, setBlogs] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedBlogs, setSelectedBlogs] = useState([]);

    // Form states
    const [mainTitle, setMainTitle] = useState('');
    const [mainDescription, setMainDescription] = useState('');
    const [cta, setCta] = useState('');
    const [subInfo, setSubInfo] = useState([{ sub_title: '', sub_desciption: '', sub_ads: false }]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [fileProcessing, setFileProcessing] = useState({});
    const [fileStatus, setFileStatus] = useState({});
    const [error, setError] = useState(null);
    const [editBlog, setEditBlog] = useState(null);
    const [blogStatus, setBlogStatus] = useState(true);

    useEffect(() => {
        fetchBlogs();
    }, [page, limit, statusFilter]);

    const fetchBlogs = async (search = '') => {
        setLoader(true);
        try {
            const resp = await Calling.getBlog(page, limit, search, statusFilter);
            if (resp?.status) {
                setBlogs(resp?.data || []);
                setTotalPages(resp?.totalPages || 1);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to fetch blogs');
        } finally {
            setLoader(false);
        }
    };

    const handleSearch = () => {
        setPage(1);
        fetchBlogs(searchQuery);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
    };

    const handleClick = (id) => {
        navigate(`/manage/blog/${id}`);
    };

    const handleSelectBlog = (id) => {
        if (selectedBlogs.includes(id)) {
            setSelectedBlogs(selectedBlogs.filter((blogId) => blogId !== id));
        } else {
            setSelectedBlogs([...selectedBlogs, id]);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedBlogs(blogs.map((blog) => blog._id));
        } else {
            setSelectedBlogs([]);
        }
    };

    // Sub Info handlers
    const addSubInfo = () => {
        setSubInfo([...subInfo, { sub_title: '', sub_desciption: '', sub_ads: false }]);
    };

    const removeSubInfo = (index) => {
        if (subInfo.length > 1) {
            const updatedSubInfo = subInfo.filter((_, i) => i !== index);
            setSubInfo(updatedSubInfo);
        }
    };

    const handleSubInfoChange = (index, field, value) => {
        const updatedSubInfo = [...subInfo];
        updatedSubInfo[index][field] = value;
        setSubInfo(updatedSubInfo);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const fieldName = event.target.name || 'image';

        setError(null);
        setSelectedFile(null);
        setFileProcessing({ ...fileProcessing, [fieldName]: true });
        setFileStatus({ ...fileStatus, [fieldName]: null });
        setPreviewImage(null);

        if (!file) {
            setError('No file selected.');
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        if (!imageType.includes(file.type)) {
            setError(invalidImageMessage);
            toast.error(invalidImageMessage);
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        if (file.size > MAX_FILE_SIZE_IMAGE) {
            setError(imageSizeExceededMessage);
            toast.error(imageSizeExceededMessage);
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        setSelectedFile(file);
        setFileProcessing({ ...fileProcessing, [fieldName]: false });
        setFileStatus({ ...fileStatus, [fieldName]: 'success' });

        const previewUrl = URL.createObjectURL(file);
        setPreviewImage(previewUrl);
        setShowPreview(true);
    };

    const resetForm = () => {
        setMainTitle('');
        setMainDescription('');
        setCta('');
        setSubInfo([{ sub_title: '', sub_desciption: '', sub_ads: false }]);
        setSelectedFile(null);
        setPreviewImage(null);
        setShowPreview(false);
        setError(null);
        setFileStatus({});
        setEditBlog(null);
        setBlogStatus(true);
    };

    const handleSubmit = async (e, blogId = null) => {
        e.preventDefault();

        if (!blogId && !selectedFile) {
            setError('Blog image is required.');
            toast.error('Blog image is required.');
            return;
        }

        if (!mainTitle || !mainDescription) {
            setError('Title and description are required.');
            toast.error('Title and description are required.');
            return;
        }

        // Validate sub_info: at least one section should have a title
        const validSubInfo = subInfo.filter(item => item.sub_title.trim() !== '');
        if (validSubInfo.length === 0) {
            setError('At least one sub-section with a title is required.');
            toast.error('At least one sub-section with a title is required.');
            return;
        }

        setLoader(true);
        try {
            const formData = new FormData();

            if (selectedFile) {
                formData.append('image', selectedFile);
            }

            formData.append('main_title', mainTitle.trim());
            formData.append('main_description', mainDescription.trim());
            formData.append('cta', cta.trim());

            // Send sub_info as JSON string
            formData.append('sub_info', JSON.stringify(validSubInfo));

            if (blogId) {
                formData.append('status', blogStatus);
                formData.append('id', blogId);
            }

            let resp;
            if (blogId) {
                resp = await Calling.updateBlog(formData);
            } else {
                resp = await Calling.addBlog(formData);
            }

            if (resp?.status) {
                toast.success(resp?.message);
                resetForm();
                await fetchBlogs(searchQuery);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("Something went wrong.");
        } finally {
            setLoader(false);
        }
    };

    const handleEdit = (blog) => {
        setEditBlog(blog);
        setMainTitle(blog.main_title);
        setMainDescription(blog.main_description);
        setCta(blog.cta || '');

        // Load sub_info or set default
        if (blog.sub_info && blog.sub_info.length > 0) {
            setSubInfo(blog.sub_info);
        } else {
            setSubInfo([{ sub_title: '', sub_desciption: '', sub_ads: false }]);
        }

        setBlogStatus(blog.status);
        setPreviewImage(blog.main_img);
        setShowPreview(true);
        setFileStatus({});

        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteSelected = async () => {
        if (selectedBlogs.length === 0) {
            return toast.error('Select at least one blog to delete.');
        }

        const content = 'You are about to delete selected blogs. Solve the math problem to confirm.';
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            const resp = await Calling.deleteBlog({ id: selectedBlogs });
            if (resp?.status) {
                toast.success(resp?.message);
                setBlogs(blogs.filter((blog) => !selectedBlogs.includes(blog._id)));
                setSelectedBlogs([]);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to delete blogs.');
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateStatus = async (id) => {
        const content = "You are about to update this blog's status. Solve the math problem to confirm.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            const currentBlog = blogs.find((blog) => blog._id === id);
            if (!currentBlog) return;

            const formData = new FormData();
            formData.append('main_title', currentBlog.main_title);
            formData.append('main_description', currentBlog.main_description);
            formData.append('cta', currentBlog.cta || '');

            if (currentBlog.sub_info && currentBlog.sub_info.length > 0) {
                formData.append('sub_info', JSON.stringify(currentBlog.sub_info));
            }

            formData.append('status', !currentBlog.status);
            formData.append('id', id);

            const resp = await Calling.updateBlog(formData);
            if (resp?.status) {
                toast.success(resp?.message);
                fetchBlogs(searchQuery);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to update status.');
        } finally {
            setLoader(false);
        }
    };

    const closePreview = () => {
        setShowPreview(false);
        setPreviewImage(null);
    };

    return (
        <>
            {loader && <Loader loader={loader} />}

            <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                <div className="mx-auto">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Blog Management</h1>
                            <p className="mt-1 text-sm text-gray-500">Manage your articles and content</p>
                        </div>
                        <button
                            onClick={() => fetchBlogs(searchQuery)}
                            className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/80 rounded-lg text-white transition-colors duration-200 shadow-sm"
                        >
                            <HiOutlineRefresh className="text-lg" />
                            <span>Refresh</span>
                        </button>
                    </div>

                    {/* Add/Edit Blog Card */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100 mb-6">
                        <div className="p-5 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                    <FaUpload className="text-main-blue text-xl" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-800">
                                        {editBlog ? 'Edit Blog' : 'Add New Blog'}
                                    </h2>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {editBlog ? 'Update existing blog post' : 'Create a new blog post'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="p-5 bg-gray-50/50">
                            <form onSubmit={(e) => handleSubmit(e, editBlog?._id)} className="space-y-4">
                                {/* Image Upload */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaImage className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                Blog Image
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Upload main blog image</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="relative">
                                            <FileUpload
                                                name="image"
                                                id="image"
                                                onChange={handleFileChange}
                                                accept={imageType.join(',')}
                                                required={!editBlog}
                                                multiple={false}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                            />
                                            {fileProcessing['image'] && (
                                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                    <div className="loader"></div>
                                                </div>
                                            )}
                                            {fileStatus['image'] === 'error' && (
                                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-red-500">✘</div>
                                            )}
                                            {fileStatus['image'] === 'success' && (
                                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-green-500">✓</div>
                                            )}
                                        </div>
                                        <p className="text-xs mt-1 text-gray-400">
                                            Allow png, jpg, jpeg / Max size: {MAX_FILE_SIZE_IMAGE / (1024 * 1024)} MB
                                        </p>

                                        {showPreview && previewImage && (
                                            <div className="mt-3 relative">
                                                <div className="border rounded-lg p-2 inline-block relative">
                                                    <button
                                                        type="button"
                                                        onClick={closePreview}
                                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                    <img
                                                        src={previewImage}
                                                        alt="Preview"
                                                        className="h-24 w-auto object-contain rounded"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Title */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaEdit className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                Blog Title
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Enter main blog title</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <input
                                            type="text"
                                            value={mainTitle}
                                            onChange={(e) => setMainTitle(e.target.value)}
                                            required
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                            placeholder="Enter blog title"
                                        />
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaEdit className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                Description
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Enter blog description</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <textarea
                                            value={mainDescription}
                                            onChange={(e) => setMainDescription(e.target.value)}
                                            required
                                            rows={4}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                            placeholder="Enter blog description"
                                        />
                                    </div>
                                </div>

                                {/* CTA */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaEdit className="text-main-blue text-sm" />
                                            <label className="block text-sm font-medium text-gray-700">
                                                Call to Action
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-2">Optional CTA text</p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <input
                                            type="url"
                                            value={cta}
                                            onChange={(e) => setCta(e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                            placeholder="Enter call to action"
                                        />
                                    </div>
                                </div>

                                {/* Dynamic Sub Info Sections */}
                                <div className="border-t pt-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="text-md font-semibold text-gray-800">Sub Sections</h3>
                                            <p className="text-xs text-gray-500">Add multiple content sections to your blog</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={addSubInfo}
                                            className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                                        >
                                            <FaPlus className="text-sm" />
                                            Add Section
                                        </button>
                                    </div>

                                    {subInfo.map((item, index) => (
                                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="text-sm font-medium text-gray-700">Section {index + 1}</h4>
                                                {subInfo.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSubInfo(index)}
                                                        className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm"
                                                    >
                                                        <FaMinus className="text-xs" />
                                                        Remove
                                                    </button>
                                                )}
                                            </div>

                                            <div className="space-y-3">
                                                {/* Sub Title */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                        Section Title *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={item.sub_title}
                                                        onChange={(e) => handleSubInfoChange(index, 'sub_title', e.target.value)}
                                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue text-sm"
                                                        placeholder="Enter section title"
                                                    />
                                                </div>

                                                {/* Sub Description */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                        Section Description
                                                    </label>
                                                    <textarea
                                                        value={item.sub_desciption}
                                                        onChange={(e) => handleSubInfoChange(index, 'sub_desciption', e.target.value)}
                                                        rows={3}
                                                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue text-sm"
                                                        placeholder="Enter section description"
                                                    />
                                                </div>

                                                {/* Sub Ads Toggle */}
                                                <div className="flex items-center gap-2">
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.sub_ads}
                                                            onChange={(e) => handleSubInfoChange(index, 'sub_ads', e.target.checked)}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                                            peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                            after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                            after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                            after:h-5 after:w-5 after:transition-all 
                                                            peer-checked:bg-main-blue"></div>
                                                    </label>
                                                    <span className="text-xs text-gray-600">
                                                        {item.sub_ads ? 'Ads Enabled' : 'Ads Disabled'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {error && (
                                    <div className="text-sm text-red-500 bg-red-50 p-3 rounded-lg">
                                        {error}
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        Reset
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 transition-colors duration-200 flex items-center gap-2"
                                    >
                                        <FaUpload className="text-sm" />
                                        {editBlog ? 'Update Blog' : 'Save Blog'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Blogs List Card */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
                        <div className="p-5 border-b border-gray-100">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                        <FaRegEye className="text-main-blue text-xl" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Blog Articles</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">Manage your existing blog posts</p>
                                    </div>
                                </div>

                                <div className="flex gap-2 items-center flex-wrap">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search title/description"
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    />
                                    <button
                                        onClick={handleSearch}
                                        className="px-4 py-2 bg-main-blue text-white rounded-lg flex items-center gap-2 hover:bg-main-blue/80"
                                    >
                                        <FaSearch /> Search
                                    </button>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    >
                                        <option value="">All</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>

                                    {selectedBlogs.length > 0 && (
                                        <button
                                            onClick={handleDeleteSelected}
                                            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                        >
                                            <FaTrash /> Delete ({selectedBlogs.length})
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="p-5 bg-gray-50/50">
                            {blogs.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-6 py-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedBlogs.length === blogs.length && blogs.length > 0}
                                                        onChange={handleSelectAll}
                                                        className="h-4 w-4 text-main-blue focus:ring-main-blue border-gray-300 rounded"
                                                    />
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Image
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Title
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Description
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    CTA
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Views
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {blogs.map((blog) => (
                                                <tr key={blog._id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedBlogs.includes(blog._id)}
                                                            onChange={() => handleSelectBlog(blog._id)}
                                                            className="h-4 w-4 text-main-blue focus:ring-main-blue border-gray-300 rounded"
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <img
                                                            src={blog.main_img}
                                                            alt={blog.main_title}
                                                            className="h-12 w-20 object-cover rounded"
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                        {blog.main_title}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">
                                                        {blog.main_description}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">
                                                        {blog.cta ? (
                                                            <button
                                                                onClick={() => window.open(blog.cta, "_blank")}
                                                                className="flex items-center justify-center px-2 py-1 bg-blue-100 text-main-blue rounded-full hover:bg-blue-200"
                                                                title="Open Link"
                                                            >
                                                                <TbNetwork size={18} />
                                                            </button>
                                                        ) : (
                                                            <span className="px-2 py-1 bg-gray-200 text-gray-500 rounded-full">N/A</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-main-blue cursor-pointer" onClick={() => navigate(`/views/dashboard/${blog?._id}`)}>
                                                        {formatView(blog.views)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={blog.status}
                                                                onChange={() => handleUpdateStatus(blog._id)}
                                                                className="sr-only peer"
                                                            />
                                                            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                                                peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                                after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                                after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                                after:h-5 after:w-5 after:transition-all 
                                                                peer-checked:bg-main-blue"></div>
                                                        </label>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium flex gap-2">
                                                        <button
                                                            onClick={() => handleClick(blog._id)}
                                                            className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100"
                                                        >
                                                            <FaRegEye />
                                                        </button>
                                                        <button
                                                            onClick={() => handleEdit(blog)}
                                                            className="px-2 py-1 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Pagination */}
                                    <div className="flex justify-between items-center mt-4">
                                        <button
                                            onClick={() => handlePageChange(page - 1)}
                                            disabled={page === 1}
                                            className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Prev
                                        </button>
                                        <span className="text-sm text-gray-600">
                                            Page {page} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => handlePageChange(page + 1)}
                                            disabled={page === totalPages}
                                            className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <NotFound />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Blog;